import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { apiError } from "../../lib/apiError";

type SecurityAlert = {
  type: string;
  user_id: number | null;
  ip_address?: string | null;
  count: number;
};

const ALERT_LABELS: Record<string, string> = {
  brute_force_login: "Brute-force logowania",
  brute_force_admin_password: "Brute-force hasła administratora",
  mass_delete: "Masowe usuwanie",
  mass_report_export: "Masowy eksport raportów",
};

const ALERT_SEVERITY: Record<string, "error" | "warning"> = {
  brute_force_login: "error",
  brute_force_admin_password: "error",
  mass_delete: "warning",
  mass_report_export: "warning",
};

export function AdminSecurityPage() {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [windowHours, setWindowHours] = useState(24);
  const [error, setError] = useState<string | null>(null);

  async function load(hours = windowHours) {
    setError(null);
    const result = await api.GET("/api/platform/security-alerts", {
      params: { query: { window_hours: hours } },
    });
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać alertów bezpieczeństwa."));
      return;
    }
    const data = result.data as { alerts: SecurityAlert[]; window_hours: number };
    setAlerts(data.alerts);
    setWindowHours(data.window_hours);
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="page__content page__content--admin">
      <AdminPageIntro>
        Wykryte wzorce podejrzanej aktywności w rejestrze audytowym.
      </AdminPageIntro>

      {error ? (
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      ) : null}

      <div className="toolbar">
        <label className="field">
          <span className="field__label">Okno analizy (godziny)</span>
          <select
            className="input"
            value={windowHours}
            onChange={(event) => {
              const hours = Number(event.target.value);
              setWindowHours(hours);
              void load(hours);
            }}
          >
            <option value={6}>6 h</option>
            <option value={24}>24 h</option>
            <option value={72}>72 h</option>
            <option value={168}>168 h</option>
          </select>
        </label>
      </div>

      {alerts.length === 0 ? (
        <p className="admin-inline-alert admin-inline-alert--ok">
          Brak alertów w wybranym oknie czasowym.
        </p>
      ) : (
        <div className="admin-alerts">
          {alerts.map((alert, index) => (
            <div
              key={`${alert.type}-${alert.user_id}-${alert.ip_address ?? ""}-${index}`}
              className={`admin-inline-alert admin-inline-alert--${
                ALERT_SEVERITY[alert.type] === "error" ? "error" : "warn"
              }`}
            >
              <strong>{ALERT_LABELS[alert.type] ?? alert.type}</strong>
              {" — "}
              użytkownik {alert.user_id ?? "nieznany"}
              {alert.ip_address ? `, IP ${alert.ip_address}` : ""}
              {", "}
              {alert.count} zdarzeń
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
